import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Users,
  ListOrdered,
  Calendar,
  BarChart3,
  Settings,
  Target,
  LayoutGrid,
  ChevronLeft,
} from 'lucide-react';
import { useTeamStore } from '../store/teamStore';
import type { Sport } from '../types';
import PageHeader from './layout/PageHeader';

// Sport icons (same as Dashboard)
import FootballIcon from '../assets/icons/football.svg';
import BasketballIcon from '../assets/icons/basketball.svg';
import SoccerIcon from '../assets/icons/soccer.svg';
import BaseballIcon from '../assets/icons/baseball.svg';
import SoftballIcon from '../assets/icons/softball.svg';
import VolleyballIcon from '../assets/icons/volleyball.svg';
import HockeyIcon from '../assets/icons/hockey.svg';
import LacrosseIcon from '../assets/icons/lacrosse.svg';

const getSportIcon = (sport: Sport) => {
  switch (sport) {
    case 'Football':
      return <img src={FootballIcon} alt="Football" className="h-6 w-6" />;
    case 'Basketball':
      return <img src={BasketballIcon} alt="Basketball" className="h-6 w-6" />;
    case 'Soccer':
      return <img src={SoccerIcon} alt="Soccer" className="h-6 w-6" />;
    case 'Baseball':
      return <img src={BaseballIcon} alt="Baseball" className="h-6 w-6" />;
    case 'Softball':
      return <img src={SoftballIcon} alt="Softball" className="h-6 w-6" />;
    case 'Volleyball':
      return <img src={VolleyballIcon} alt="Volleyball" className="h-6 w-6" />;
    case 'Hockey':
      return <img src={HockeyIcon} alt="Hockey" className="h-6 w-6" />;
    case 'Lacrosse':
      return <img src={LacrosseIcon} alt="Lacrosse" className="h-6 w-6" />;
    default:
      return <LayoutGrid className="h-6 w-6" />;
  }
};

interface HubTile {
  name: string;
  href: string;
  icon: React.ElementType;
  description: string;
}

const TeamHub: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { currentTeam, allTeams, switchTeam } = useTeamStore();

  // Ensure we're viewing the correct team when landing on this page
  useEffect(() => {
    if (!teamId) return;
    const team = allTeams.find((t) => t.id === teamId);
    if (!team) {
      navigate('/dashboard', { replace: true });
      return;
    }
    if (currentTeam?.id !== teamId) {
      switchTeam(teamId);
    }
  }, [teamId, allTeams, currentTeam?.id, switchTeam, navigate]);

  const team = allTeams.find((t) => t.id === teamId);
  if (!team) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4" />
        <p className="caption">Loading team...</p>
      </div>
    );
  }

  const baseTiles: HubTile[] = [
    { name: 'Roster', href: '/players', icon: Users, description: 'Manage players and roster' },
    { name: 'Depth Chart', href: '/depth-chart', icon: ListOrdered, description: 'View and edit depth chart' },
    { name: 'Practices', href: '/practices', icon: Calendar, description: 'Plans and attendance' },
    { name: 'Settings', href: '/settings', icon: Settings, description: 'Team and app settings' },
  ];

  const sportSpecificTiles: HubTile[] = [];
  if (team.sport === 'Football') {
    sportSpecificTiles.push({
      name: 'Game Plans',
      href: '/game-plans',
      icon: Target,
      description: 'Plays and game planning',
    });
  }
  if (team.sport === 'Baseball' || team.sport === 'Softball') {
    sportSpecificTiles.push({
      name: 'Games',
      href: '/games',
      icon: BarChart3,
      description: 'Schedule and game management',
    });
  }

  const tiles = [...baseTiles];
  // Insert sport-specific tiles before Settings (last item)
  if (sportSpecificTiles.length > 0) {
    tiles.pop(); // remove Settings
    tiles.push(...sportSpecificTiles, baseTiles[baseTiles.length - 1]); // add sport tiles then Settings
  }

  return (
    <div>
      <PageHeader
        title={team.name}
        icon={getSportIcon(team.sport)}
        actions={
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-stone-600 hover:text-primary border border-stone-200 rounded-lg hover:border-primary transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            All teams
          </Link>
        }
      />

      <p className="text-stone-600 mb-6">
        You're viewing <strong className="text-dark">{team.name}</strong>. Choose an area to manage.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tiles.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className="group flex items-start gap-4 p-5 rounded-2xl border border-stone-200 bg-white hover:border-primary hover:shadow-md transition-all duration-200"
            >
              <div className="flex-shrink-0 p-3 rounded-xl bg-primary bg-opacity-10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                <Icon className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-dark group-hover:text-primary transition-colors">
                  {item.name}
                </h3>
                <p className="text-sm text-stone-500 mt-0.5">{item.description}</p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default TeamHub;
